<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAppearanceStore } from '@stores/appearance'
import { useAuthStore } from '@stores/auth'
import { useFavoritesStore } from '@stores/favorites'
import { useMessages } from '@composables/use-messages'
import GenreOptions from '@components/genre/genre-options'

const props = defineProps({
    text: {
        type: String,
        required: true,
    },
    resource: {
        type: Object,
        default: null,
    },
})

const router = useRouter()
const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const favoritesStore = useFavoritesStore()
const getMessageText = useMessages()

// Guards against a second follow being sent while the first is still in flight.
const isReady = ref(true)

const isLoggedIn = computed(() => authStore.loggedIn)

const isFollowing = computed(
    () => !!props.resource && favoritesStore.genreIds.includes(props.resource.id)
)

async function toggleFollow() {
    if (!isLoggedIn.value) {
        router.push({ name: 'login' })
        return
    }

    if (!isReady.value || !props.resource) return

    isReady.value = false

    try {
        if (isFollowing.value) {
            await favoritesStore.remove({ type: 'genres', id: props.resource.id })
            appearanceStore.handleNotification({
                message: 'Genre Unfollowed',
                type: 'success',
            })
        } else {
            await favoritesStore.add({ type: 'genres', id: props.resource.id })
            appearanceStore.handleNotification({
                message: 'Following Genre',
                type: 'success',
            })
        }
    } catch (error) {
        console.error('Error toggling genre follow:', error)
        appearanceStore.handleNotification({
            message: getMessageText('SOMETHING_WENT_WRONG'),
            type: 'warning',
        })
    } finally {
        isReady.value = true
    }
}
</script>


<template>
    <div class="container">
        <div class="inner">
            <div>
                <h6 class="type">Genre</h6>
                <BasePlaceholder
                    class="title"
                    :text="text"
                    type="genre"
                    tag="h1"
                />
                <div
                    v-if="resource"
                    class="actions"
                >
                    <BaseButton
                        :active="isFollowing"
                        variant="outline"
                        size="m"
                        :icon="isFollowing ? 'check' : ''"
                        @click="toggleFollow"
                    >
                        {{ isFollowing ? 'Following' : 'Follow' }}
                    </BaseButton>
                    <div
                        class="options"
                    >
                        <GenreOptions
                            :genre="resource"
                            :is-following="isFollowing"
                            :has-view-genre-option="false"
                            @toggle-follow="toggleFollow"
                        />
                    </div>
                </div>
            </div>
        </div>
        <BaseHeroBackground
            v-if="resource"
            :resource="resource"
            type="artwork"
        />
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.container {
    position: relative;
    z-index: $layer-highlight-z-index;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding-top: $s-xl;

    @include desktop {
        min-height: 350px;
    }
}

.inner {
    position: relative;
    z-index: $layer-page-z-index;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;

    @include container;

    @include tablet {
        flex-direction: row;
    }

    @include desktop {
        margin-top: $s-h * 0.3;
    }

    @include sidePadding;
}

.type {
    display: none;

    @include tablet {
        display: block;
    }
}

.title {
    @extend %h3;

    width: 100%;
    margin-bottom: $s-xxl;
    text-align: left;

    @include tablet {
        @include h1;

        margin-top: $s-xxxl;
    }
}

// Centre the 40px kebab on the 40px Follow pill, and clear the row off the page below —
// BaseButton carries no bottom margin of its own.
.actions {
    display: flex;
    gap: $s-m;
    align-items: center;
    justify-content: center;
    margin-top: $s-l;
    margin-bottom: $space-24;

    @include tablet {
        justify-content: flex-start;
    }
}

.options {
    position: relative;
}
</style>
